import React, { useEffect, useState } from 'react';
import { Card, ListGroup, Badge, ProgressBar, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faTriangleExclamation,
  faMinus
} from '@fortawesome/free-solid-svg-icons';
import { fetchTopRiskElements, TopRiskElement } from 'services/hvarisk.service';
import { useAppContext } from 'providers/AppProvider';

interface TopRiskElementsCardProps {
  dateRange: {
    datefrom: string;
    dateto: string;
  };
  campaignId?: number;
  categoryId?: number;
  serviceId?: number;
}

const TopRiskElementsCard: React.FC<TopRiskElementsCardProps> = ({
  dateRange,
  campaignId,
  categoryId,
  serviceId
}) => {
  const {
    config: { isDark }
  } = useAppContext();
  const [topElements, setTopElements] = useState<TopRiskElement[]>([]);
  const [maxCount, setMaxCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const params = {
          datefrom: dateRange.datefrom,
          dateto: dateRange.dateto,
          campaignid: campaignId,
          categoryid: categoryId,
          serviceid: serviceId,
          limit: 5
        };

        const data = await fetchTopRiskElements(params);
        setTopElements(data);

        if (data.length > 0) {
          setMaxCount(Math.max(...data.map(e => e.count)));
        }
      } catch (error) {
        console.error('Error loading top risk elements:', error);
        setTopElements([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [dateRange, campaignId, categoryId, serviceId]);

  const getTrendIcon = (element: TopRiskElement) => {
    if (element.trend === 'up') {
      return (
        <FontAwesomeIcon
          icon={faArrowUp}
          className="text-danger"
          title={`Increased by ${element.changePercent}%`}
        />
      );
    }
    if (element.trend === 'down') {
      return (
        <FontAwesomeIcon
          icon={faArrowUp}
          className="text-success"
          rotation={180}
          title={`Decreased by ${Math.abs(element.changePercent)}%`}
        />
      );
    }
    return (
      <FontAwesomeIcon
        icon={faMinus}
        className="text-muted"
        title="No significant change"
      />
    );
  };

  const getSeverityVariant = (severity: number) => {
    if (severity >= 4) return 'danger';
    if (severity >= 3) return 'warning';
    if (severity >= 2) return 'info';
    return 'success';
  };

  if (isLoading) {
    return (
      <Card className="h-100">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="me-2 text-primary"
            />
            Top 5 Risk Elements
          </h5>
        </Card.Header>
        <Card.Body
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '300px' }}
        >
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (topElements.length === 0) {
    return (
      <Card className="h-100">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="me-2 text-primary"
            />
            Top 5 Risk Elements detected by AI
          </h5>
        </Card.Header>
        <Card.Body
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '300px' }}
        >
          <div className="text-center text-muted">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              size="3x"
              className="mb-3 opacity-25"
            />
            <p>No risk elements found for the selected period</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="me-2 text-primary"
            />
            Top 5 Risk Elements detected by AI
          </h5>
          <small className="text-muted">
            {dateRange.datefrom} to {dateRange.dateto}
          </small>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <ListGroup variant="flush">
          {topElements.map(element => {
            const severity = parseFloat(element.avgSeverity);

            return (
              <ListGroup.Item
                key={element.id}
                className={`px-3 py-2 ${isDark ? 'bg-gray-1100' : ''}`}
              >
                <div className="d-flex align-items-start">
                  <div className="me-3 text-center" style={{ width: '30px' }}>
                    <span className="text-muted fw-semibold">
                      {element.rank}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div className="flex-1 me-2">
                        <h6 className="mb-0 fs-9 text-body-emphasis">
                          {element.name}
                        </h6>
                        <span className="text-muted fs-10">
                          {element.categoryName}
                        </span>
                      </div>
                      <div className="text-end">
                        <div className="fw-semibold text-body-emphasis">
                          {element.count}
                        </div>
                        <div className="d-flex align-items-center gap-1 fs-10">
                          {getTrendIcon(element)}
                          <span className="text-muted">
                            {Math.abs(element.changePercent)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-4">
                      <ProgressBar
                        now={(element.count / maxCount) * 100}
                        style={{ height: '4px', flex: 1 }}
                        variant={getSeverityVariant(severity)}
                        aria-label={`${element.count} occurrences`}
                      />
                      <Badge
                        pill
                        bg={getSeverityVariant(severity)}
                        className="fs-10 fw-bold"
                        title={`Average severity: ${element.avgSeverity}`}
                      >
                        {element.avgSeverity}
                      </Badge>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </Card.Body>
      <Card.Footer className="text-center py-2">
        <small className="text-muted">
          Showing top {topElements.length} risk elements by frequency
        </small>
      </Card.Footer>
    </Card>
  );
};

export default TopRiskElementsCard;
